import React from "react";
import { Button } from "@/components/ui/button";
import { FormItem, FormMessage, FormDescription } from "@/components/ui/form";
import type { FieldConfig } from "../types";
import { cn } from "@/lib/utils";
import { useLingui } from "@lingui/react/macro";
import { t } from "@lingui/macro";
import { useFormContext, useWatch } from "react-hook-form";

interface HabitOption {
  value: string;
  label: string;
}

interface SelectedHabit {
  id: string;
  applicable: boolean | null;
}

interface HabitSelectionFieldProps<T> {
  field: FieldConfig<T> & {
    habitOptions?: HabitOption[];
  };
  value: SelectedHabit[];
  onChange: (value: SelectedHabit[]) => void;
  onBlur: () => void;
  disabled?: boolean;
  id: string;
  "aria-describedby"?: string;
  className?: string;
}

export function HabitSelectionField<T>({
  field,
  value = [],
  onChange,
  onBlur,
  disabled,
  id,
  "aria-describedby": ariaDescribedBy,
  className,
}: HabitSelectionFieldProps<T>) {
  const { i18n } = useLingui();
  const options = field.habitOptions || [];

  // State for button press feedback
  const [pressedButton, setPressedButton] = React.useState<string | null>(null);

  // Ref to track timeout for cleanup
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  // Access the form context to get/set the useDiapers field
  const formMethods = useFormContext();
  const { setValue } = formMethods;
  
  // Validate that form context exists when required
  // This component requires form context for toilet training functionality
  if (!formMethods) {
    throw new Error(
      'HabitSelectionField must be used within a FormProvider. ' +
      'Please wrap your form with <FormProvider> or use the form prop from react-hook-form.'
    );
  }
  
  // Use i18n for YES/NO labels
  const yesLabel = i18n._(t({ id: 'common.yes', message: 'Yes' }));
  const noLabel = i18n._(t({ id: 'common.no', message: 'No' }));

  // Check if this is the Toilet Training card
  const isToiletTraining = field.name === 'selectedToiletHabits';
  
  // Use useWatch hook to properly watch the useDiapers field with dependency tracking
  const useDiapersValue = useWatch({
    control: formMethods.control,
    name: 'useDiapers',
    disabled: !isToiletTraining, // Only watch when it's toilet training
    defaultValue: null,
  });

  // Create a map of current selections for easy lookup
  const selectionMap = new Map(
    value.map(habit => [habit.id, habit.applicable])
  );

  // Handle button click with press feedback
  const handleButtonClick = React.useCallback(
    (optionId: string, newValue: boolean | null, buttonId: string) => {
      // Add slight press feedback
      setPressedButton(buttonId);

      // Special handling for "Uses Diapers" option
      if (optionId === '_uses_diapers_special' && isToiletTraining) {
        setValue('useDiapers', newValue, { shouldValidate: true });
        onBlur();
      } else {
        const updatedValue = value.filter(habit => habit.id !== optionId);

        // Only add back if not null (null means unselected/undefined)
        if (newValue !== null) {
          updatedValue.push({ id: optionId, applicable: newValue });
        }

        onChange(updatedValue);
        onBlur();
      }

      // Clear any existing timeout before setting a new one
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Clear press state after transition with cleanup
      timeoutRef.current = setTimeout(() => {
        setPressedButton(null);
        timeoutRef.current = null;
      }, 150);
    },
    [value, onChange, onBlur, isToiletTraining, setValue]
  );

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return (
    <FormItem className="space-y-3">
      <div className="space-y-1">
        {field.description && (
          <FormDescription id={`${id}-description`}>
            {field.description}
          </FormDescription>
        )}
      </div>
      
      <div 
        className={cn("grid grid-cols-1 sm:grid-cols-[1fr_1fr] gap-x-8 gap-y-3 items-start", className)}
        role="group"
        aria-label={i18n._(t`Select Habits`)}
        aria-describedby={cn(
          ariaDescribedBy,
          field.description && `${id}-description`
        )}
      >
        {options.map((option) => {
          // For the special "Uses Diapers" option in toilet training, get value from useDiapers field
          const currentValue = (isToiletTraining && option.value === '_uses_diapers_special')
            ? useDiapersValue ?? null
            : selectionMap.get(option.value) ?? null;
          const optionId = `${id}-${option.value}`;
          
          // Create handler inline - it's fine since handleToggle is memoized
          const handleButtonPress = (newValue: boolean) => {
            // If already selected, deselect to null; otherwise select the new value
            const finalValue = currentValue === newValue ? null : newValue;
            const buttonId = `${optionId}-${newValue ? 'yes' : 'no'}`;
            handleButtonClick(option.value, finalValue, buttonId);
          };
          
          return (
            <React.Fragment key={option.value}>
              {/* Label in first column */}
              <label 
                htmlFor={`${optionId}-yes`}
                className="text-sm font-medium py-1.5 cursor-pointer"
              >
                {option.label}
              </label>
              
              {/* Buttons in second column */}
              <div className="flex gap-2 w-full">
                <Button
                  id={`${optionId}-yes`}
                  type="button"
                  variant={currentValue === true ? "default" : "outline"}
                  onClick={() => handleButtonPress(true)}
                  disabled={disabled}
                  className={cn(
                    "flex-1 transition-all duration-150 ease-in-out",
                    currentValue === true && "font-semibold",
                    pressedButton === `${optionId}-yes` && "scale-[0.98]"
                  )}
                  aria-pressed={currentValue === true}
                  aria-label={`${option.label} ${yesLabel}`}
                >
                  {yesLabel}
                </Button>
                <Button
                  id={`${optionId}-no`}
                  type="button"
                  variant={currentValue === false ? "destructive" : "outline"}
                  onClick={() => handleButtonPress(false)}
                  disabled={disabled}
                  className={cn(
                    "flex-1 transition-all duration-150 ease-in-out",
                    currentValue === false && "font-semibold",
                    pressedButton === `${optionId}-no` && "scale-[0.98]"
                  )}
                  aria-pressed={currentValue === false}
                  aria-label={`${option.label} ${noLabel}`}
                >
                  {noLabel}
                </Button>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      
      <FormMessage />
    </FormItem>
  );
}